import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { of, Observable, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { DealerForm } from '../models/forms/dealer.form';
import { MergeDealerForm } from '../models/forms/merge-dealer.form';
import { League } from '../models/league';
import { LeagueStorage } from '../models/league.storage';
import { UUID } from '../models/uuid';

import { GenericService } from './generic.service';
import { PageableService } from './pageable.service';
import { GetDealersResponse, GetDealerResponse } from './responses/dealer.responses';
import { GetPagedResponse, NameExistsResponse } from './responses/generic.responses';

@Injectable()
export class DealerService extends GenericService implements PageableService {
  league: League;
  _propertySubscription: Subscription;
  constructor(
    private http: HttpClient,
    private leagueStorage: LeagueStorage,
    protected logger: NGXLogger
  ) {
    super(logger);
    this._propertySubscription = leagueStorage.getSelectedLeague().subscribe(league => {
      this.league = league;
    });
  }

  public checkNameTaken(name: string): Observable<boolean> {
    // if the current league is null (could happen during init) it's fine.
    if (this.league == null) {
      return of(false);
    }

    return this.http
      .post<NameExistsResponse>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer/exists`,
        { name },
        this.httpOptions
      )
      .pipe(map(r => r.exists));
  }

  public getPagedData(params: any): Observable<GetPagedResponse> {
    return this.getDealers(params).pipe(
      map(r => {
        return {
          data: r.dealers,
          pagination: r.pagination
        };
      })
    );
  }

  public getDealers(params: any): Observable<GetDealersResponse> {
    this.logger.debug('Called getDealers');
    if (this.league == null) {
      this.logger.debug('returning default dealers');
      return of(this.defaultResponse('dealers'));
    }
    this.logger.debug('calling get dealers service');

    const options = {
      headers: this.httpOptions.headers,
      params: new HttpParams({ fromObject: params })
    };

    return this.http
      .get<GetDealersResponse | any>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer`,
        options
      )
      .pipe(tap(response => this.logger.debug('getDealers response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getDealer(dealerId: UUID): Observable<GetDealerResponse | any> {
    this.logger.debug('Called getDealer');
    if (this.league == null) {
      this.logger.debug('returning default dealer');
      return of({ dealer: null });
    }

    this.logger.debug('calling get dealer service');
    return this.http
      .get<GetDealerResponse>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer/${dealerId}`,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('getDealer response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public createDealer(value: DealerForm): Observable<GetDealerResponse | any> {
    this.logger.debug('Called createDealer');
    return this.http
      .post<GetDealerResponse>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer`,
        value,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('createDealer response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateDealer(value: DealerForm): Observable<GetDealerResponse | any> {
    this.logger.debug('Called updateDealer');
    return this.http
      .patch<GetDealerResponse>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer/${value.id}`,
        value,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('updateDealer response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public mergeDealer(value: MergeDealerForm): Observable<GetDealerResponse | any> {
    this.logger.debug('Called mergeDealer');
    return this.http
      .post<GetDealerResponse>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer/${value.id}/merge`,
        value,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('mergeDealer response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public deleteDealer(dealerId: UUID): Observable<any | any> {
    this.logger.debug('Called deleteDealer');
    return this.http
      .delete<any>(
        `${environment.DEALERS_API}/league/${this.league.id}/dealer/${dealerId}`,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('deleteDealer response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }
}
